import React, { useState, useRef, useEffect } from "react";
import styles from "../SignUp/SignUp.module.css";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import {
  API_BASE_URL,
  fetchAgentDetailById,
  LoginWithEmailOTP,
  verifyEmailOTP,
  // finalizeShopifyConnection,
} from "../../Store/apiStore";

import { useLayoutEffect } from "react";
import PopUp from "../Popup/Popup";
import Loader from "../Loader/Loader";
import useUser from "../../Store/Context/UserContext";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import useUserDeviceInfo from "../../hooks/useUserDeviceInfo";
import detectPlatform from "../../utils/detectPlatform";
import useUTMParams from "../../hooks/useUTMParams";
import useOnboardingTracker from "../../hooks/useOnboardingTracker";
import useSkipDetails from "../../hooks/useSkipDetails";
import decodeToken from "../../lib/decodeToken";
import { setGAUser, trackEvent, setUserProperties, fireAuthEvent, isAuthEventRecorded } from "../../lib/analytics";
import { syncStoredPushToken } from "../../utils/initNotificationSocket";

import useRecaptchaV3 from "../../hooks/RecaptchaV3";
import SecureRoute from "../../Pages/SecureRoute";
import axios from "axios";
import spamDomains from "../../utils/spamDomains";
import { LoaderCircle } from "lucide-react";
import { useIPRestriction } from "../../hooks/useIPRestriction";
import Loader2 from "../Loader2/Loader2";
import { useAgentStore } from "../../Store/agentDetailStore";
import REGION_BLOCK_MESSAGE from "../../alert/alert";

// An AppSumo signup is one whose UTM names appsumo as the source and carries a
// tier in utm_campaign. Both must be present: the tier is what decides how many
// minutes the code grants, so without it there is nothing to redeem into.
const APPSUMO_SOURCES = ["appsumo", "sumo", "app-sumo"];

// Reads the UTM from the live URL, then localStorage (where useUTMParams keeps
// it), then the hook's state. The URL wins because a redemption link opened in
// a fresh tab has not reached localStorage yet; localStorage covers the case
// where the query string was dropped by an earlier navigation.
const resolveSignupUtm = (fromHook) => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {};
  for (const key of ["utm_source", "utm_campaign"]) {
    const v = params.get(key);
    if (v) fromUrl[key] = v;
  }
  if (fromUrl.utm_source || fromUrl.utm_campaign) return fromUrl;

  try {
    const stored = JSON.parse(localStorage.getItem("utm_data") || "null");
    if (stored && typeof stored === "object") return stored;
  } catch {
    /* unparseable localStorage is the same as none */
  }
  return fromHook || null;
};

const isAppSumoSignup = (utm) => {
  if (!utm) return false;
  const source = String(utm.utm_source || "").trim().toLowerCase();
  if (!APPSUMO_SOURCES.includes(source)) return false;
  const campaign = String(utm.utm_campaign || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  return ["tier1", "tier2", "tier3"].includes(campaign);
};

const SignUp = () => {
  const navigate = useNavigate();
  // New users skip /details and go straight to /business-step (background —
  // UTM / ReactNative / verifyDetails — still runs). Mirrors Onboarding.
  const { skipToBusinessStep } = useSkipDetails();
  const [searchParams] = useSearchParams();
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const executeRecaptcha = useRecaptchaV3(siteKey);
  const [otpSent, setOtpSent] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [otp, setOtp] = React.useState(["", "", "", "", "", ""]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [isLoadingG, setLoadingG] = useState(false);

  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState(false);
  const inputRefs = useRef([]);
  const { user, setUser } = useUser();
  const [resendTimer, setResendTimer] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resendEndTime, setResendEndTime] = useState(null);
  const tempReferral = sessionStorage.getItem("referredBy") || "";
  const tempLandingSelectedPlan = sessionStorage.getItem("selectedPlan") || "";
  const tempReferredByName = sessionStorage.getItem("referredByName") || "";
  const isUser = sessionStorage.getItem("isUser") || "";
  const [customerId, setCustomerId] = useState();
  const [renderHtml, setRenderHtml] = useState(false);
  const info = useUserDeviceInfo();
  const { isBlocked, loading: ipLoading, ipInfo } = useIPRestriction(info);
  const utm_data = useUTMParams();
  const listRef = useRef(null);
  const { steps, trackStep } = useOnboardingTracker();
  const [userDetails, setUserDetails] = useState();
  const didVerifyOtpRef = useRef(false);
  const isRedirectingAfterOtpRef = useRef(false);
  // Google One Tap (web browsers only)
  const googleBtnRef = useRef(null);
  const gsiInitialized = useRef(false);
  const [gsiReady, setGsiReady] = useState(false);
  const [otpInlineError, setOtpInlineError] = useState("");
  const {
    agentData,
    assignedNumbers,
    totalBookings,
    setAgentById,
    setCurrentAgentId,
    getAgentById,
  } = useAgentStore();

  const [loadingForAuth, setLoadingForAuth] = useState(false);

  const location = useLocation();
  // primeSingleAgentContext lived here and duplicated what resolveHomeRoute
  // already does. Team logins now route through /select-access, which lands via
  // resolveHomeRoute — and that helper primes SelectAgentId /
  // SelectAgentBusinessId / SelectAgentOwnerId itself.

  // Check immediately if there's a token in URL or localStorage
  useEffect(() => {
    if (isRedirectingAfterOtpRef.current) return;
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");

    // Set loading to true immediately if token exists
    if (tokenFromUrl) {
      setLoadingForAuth(true);
      // Clear Google auth timestamp since we have a token
      sessionStorage.removeItem('googleAuthStartTime');
      setLoadingG(false);

      // Safety timeout: if redirect doesn't happen in 10 seconds, show signup page
      const timeoutId = setTimeout(() => {
        console.warn("Auth redirect timeout - showing signup page");
        setLoadingForAuth(false);
      }, 10000);

      return () => clearTimeout(timeoutId);
    } else {
      setLoadingForAuth(false);
    }
  }, [location.search]);

  useEffect(() => {
    const emailFromParams = searchParams.get("email");
    if (emailFromParams) {
      localStorage.removeItem("token");
      localStorage.removeItem("customerId");
      localStorage.removeItem("userId");
    }
  }, [searchParams]);

  // Shopify App Store (Flow 1): the OAuth callback redirects a brand-new merchant
  // here carrying the buffered handoff token + shop + owner email. Stash the token
  // in sessionStorage so it survives the OTP steps, and pre-fill the email field.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const handoff = params.get("shopify_handoff_token");
    if (!handoff) return;
    sessionStorage.setItem("shopify_handoff_token", handoff);
    const shopParam = params.get("shop");
    if (shopParam) sessionStorage.setItem("shopify_shop", shopParam);
    const emailParam = params.get("email");
    if (emailParam) setEmail(emailParam.toLowerCase());
  }, [location.search]);

  useEffect(() => {
    if (isRedirectingAfterOtpRef.current) return;
    const params = new URLSearchParams(location.search);

    const tokenFromUrl = params.get("token");
    const tokenFromAgentId = params.get("agent_id");

    const tokenFromBusinessId = params.get("businessId");

    const customerIdFromUrl = params.get("customerId") || "";
    const userIdFromUrl = params.get("userId") || "";
    const jobberLogin = params.get("jobberLogin") === "true";
    const jobberExisting = params.get("jobberExisting") === "true";
    const fetchEmail = params.get("email")
    const fetchUserDetailsAndRoute = async (tokenToUse) => {
      if (didVerifyOtpRef.current) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/getUserDetails`,
          { headers: { Authorization: `Bearer ${tokenToUse}` } }
        );

        const user = res.data.user;
        // console.log("user", user);

        // GA4: identify the user and record signup/login (Google / Jobber flow).
        setGAUser(decodeToken(tokenToUse)?.id || userIdFromUrl);
        setUserProperties({
          account_type: user?.paymentDone ? "paid" : "free_test",
          ...(user?.paymentDone ? {} : { plan: "none" }),
        });
        fireAuthEvent({
          isExisting: user?.verifyDetails,
          method: jobberLogin ? "jobber" : "google",
          token: tokenToUse,
        });
        // GA4: onboarding_start right after account creation (see email/OTP flow
        // for why). Only for brand-new signups, not existing-user logins.
        if (!user?.verifyDetails) {
          trackEvent("onboarding_start", {
            entry_point: jobberLogin ? "jobber" : "google",
          });
        }

        const userData = {
          name: user?.name || "",
          profile: user?.profile || "images/camera-icon.avif",
          subscriptionDetails: {},
        };
        setUser(userData);
        syncStoredPushToken(user?.userId || user?.id || decodeToken(tokenToUse)?.id);

        if (user?.email) {
          sessionStorage.setItem("jobberIntegrationEmail", user.email);
          localStorage.setItem("userEmail", user.email);
        }

        // NOTE: do NOT return to Make here. Google/Jobber routes into the app the
        // SAME way as email; the return to Make happens later from FastAgentDetails,
        // ONLY once an agent exists (count >= 1). The preserved make_oauth_popup tag
        // makes that later return possible. Returning here would bounce a brand-new
        // user back to Make with zero agents ("No data available").

        // ✅ TEAM MEMBER CHECK — multi-owner: land on the grouped Dashboard so
        // agents from every owner who added them are visible.
        if (user?.isTeamMember) {
          localStorage.setItem("onboardComplete", "true");
          if (user?.ownerId) localStorage.setItem("teamOwnerId", user.ownerId);
          // A team member may hold several kinds of access (their own account,
          // admin on one company, support on another). Let them choose which one
          // this session runs as instead of silently using whichever membership
          // the server happened to resolve first. /select-access forwards on its
          // own when there is genuinely only one option, so the common case
          // still costs no extra tap.
          navigate("/select-access", { replace: true });
          return;
        }

        // A previously removed member can now use this retained account as a
        // normal user. Do not let a stale browser hint keep them in team mode.
        localStorage.removeItem("teamOwnerId");

        localStorage.setItem("onboardComplete", user?.verifyDetails);

        // ✅ JOBBER FLOW
        if (jobberLogin) {
          if (jobberExisting) {
            navigate("/fast-agent-detail", { replace: true });
          } else {
            skipToBusinessStep({ replace: true, email: user?.email });
          }
          return;
        }

        // ✅ SINGLE AGENT FLOW (Google Login — mirrors OTP logic)
        if (tokenFromAgentId) {
          sessionStorage.setItem("SelectAgentId", tokenFromAgentId);
          sessionStorage.setItem("SelectAgentBusinessId", tokenFromBusinessId || "");
          await getAgentDetailsAndBookings(tokenFromAgentId, tokenFromBusinessId);
          if (user?.verifyDetails) navigate("/fast-agent-detail", { replace: true });
          else skipToBusinessStep({ replace: true, email: user?.email });
          return;
        }

        // ✅ NORMAL FLOW
        if (user?.verifyDetails) {
          navigate("/fast-agent-detail", { replace: true });
        } else {
          // Skip /details — straight to /business-step.
          skipToBusinessStep({ replace: true, email: user?.email });
        }
      } catch (err) {
        console.error("❌ Error fetching user details:", err);
        setLoadingForAuth(false); // Reset loading on error
        navigate("/signup", { replace: true });
      }
    };

    // 🔹 Token from URL (Jobber / Google)
    if (tokenFromUrl) {
      localStorage.setItem("token", tokenFromUrl);

      // Shopify Flow 1 (Google/Jobber login): if a handoff token was stashed on
      // mount, finish linking the store now that we have a JWT. sessionStorage
      // survives the Google redirect (same tab). Fire-and-forget — never block.
      // commented for dev-server code stop

      // const shopifyHandoff = sessionStorage.getItem("shopify_handoff_token");
      // if (shopifyHandoff) {
      //   finalizeShopifyConnection(shopifyHandoff)
      //     .then((r) => console.log("[shopify] connection finalized (google):", r?.data))
      //     .catch((e) => console.warn("[shopify] finalize failed:", e?.response?.data || e.message))
      //     .finally(() => sessionStorage.removeItem("shopify_handoff_token"));
      // }

      localStorage.setItem("customerId", customerIdFromUrl);
      localStorage.setItem("userId", userIdFromUrl);
      localStorage.setItem("fetchEmail", fetchEmail)
      localStorage.setItem("tokenFromAgentId", tokenFromAgentId)

      localStorage.setItem("businessId", tokenFromBusinessId)

      // Preserve the Make OAuth popup tag across this wipe — same guarded pattern
      // as the email/OTP flow (handleLoginClick) and SecureRoute. This is the
      // Google/Jobber return path (token arrives in the URL after the full-page
      // /auth/google redirect). The tag is per-window sessionStorage, so ONLY the
      // Make consent popup ever has it; a normal signup tab reads null and the
      // `if` below restores NOTHING → sessionStorage is fully cleared exactly like
      // before. Without this, a user who signs in with Google INSIDE the Make
      // popup loses the tag and never auto-returns to Make (returnToProvider bails
      // when the tag is missing).
      const makePopupTag = sessionStorage.getItem("make_oauth_popup");
      sessionStorage.clear();
      if (makePopupTag) sessionStorage.setItem("make_oauth_popup", makePopupTag);

      fetchUserDetailsAndRoute(tokenFromUrl);
      return;
    }

    // App deep-link fallback: on mobile the login callback redirects to a
    // com.rexptin://signup?token=… deep link and the native shell may hand the
    // JWT to the WebView via localStorage WITHOUT a ?token= URL param. In that
    // case the URL path above never runs, so sign_up/login was never fired.
    // Detect a stored token whose auth event hasn't been recorded yet, look up
    // new-vs-existing, and fire it once (keyed by token in fireAuthEvent).
    const storedToken = localStorage.getItem("token");
    if (tokenFromUrl) {
      fetchUserDetailsAndRoute(tokenFromUrl);
    } else if (storedToken && !isAuthEventRecorded()) {
      (async () => {
        try {
          const uid = decodeToken(storedToken)?.id;
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/auth/getUserDetails`,
            { headers: { Authorization: `Bearer ${storedToken}` } }
          );
          setGAUser(uid);
          fireAuthEvent({
            isExisting: res?.data?.user?.verifyDetails === true,
            method: "google",
            token: storedToken,
          });
        } catch (e) {
          // Non-blocking — analytics must never break auth/routing.
        } finally {
          setLoadingForAuth(false);
        }
      })();
    } else {
      setLoadingForAuth(false);
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const verifiedDetails = searchParams.get("verifiedDetails");
    const tokenFromParams = searchParams.get("token");
    if (tokenFromParams) {
      localStorage.setItem("token", tokenFromParams);
      localStorage.setItem("onboardComplete", verifiedDetails);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!resendEndTime) return;

    const interval = setInterval(() => {
      const timeLeft = Math.max(
        0,
        Math.floor((resendEndTime - Date.now()) / 1000)
      );
      setResendTimer(timeLeft);

      if (timeLeft <= 0) {
        setIsResendDisabled(false);
        setResendEndTime(null);
        localStorage.removeItem("otpResendEndTime");
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [resendEndTime]);

  const validateEmail = (email) => {
    if (!email) {
      if (!emailTouched || emailSubmitted) {
        return "Email is required.";
      }
      return "";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address.";
    return "";
  };

  const handleEmailChange = (e) => {
    const val = e.target.value.toLowerCase().trim();
    setEmail(val);
    setEmailTouched(true);

    if (showPopup) setShowPopup(false);

    if (emailSubmitted) {
      setEmailError(validateEmail(val));
    } else {
      setEmailError("");
    }
  };
  const getAgentDetailsAndBookings = async (agentID, agentBuisnesId) => {
    const agentDetails = {
      agentId: agentID,
      bussinesId: agentBuisnesId
    };
    const cached = getAgentById(agentID);
    if (cached) {
      setCurrentAgentId(agentID);
    }
    try {
      const response = await fetchAgentDetailById(agentDetails);
      const agentInfo = response?.data;
      let numbersArray = [];
      let total = 0;
      setAgentById(agentDetails.agentId, agentInfo, numbersArray, total);
      // setName(agentInfo?.agent?.agentName);
    } catch (err) {
      console.error("Failed to fetch data", err.response || err.message || err);
    }
  };
  const handleLoginClick = async () => {
    const emailValidationMsg = validateEmail(email);
    setEmailError(emailValidationMsg);

    if (emailValidationMsg) return;

    const fullOtp = otp.join("");
    if (fullOtp.length !== 6) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Please enter a valid 6-digit OTP.");
      setRenderHtml(false);
      return;
    }

    // GA4: Continue pressed on the OTP screen with all 6 digits filled —
    // fires at the click, before the server verifies the code.
    trackEvent("otp_continue_click", { screen: "otp_verify" });

    setIsVerifyingOtp(true);

    try {
      const response = await verifyEmailOTP(
        email.toLowerCase(),
        fullOtp,
        customerId
      );
      // console.log(response, "response")

      if (response?.status === 200) {
        // 🌍 Region gate — we only serve IN / US / CA. If the user logs in
        // from any other region, stop here and show a polite message instead
        // of entering the app. `isBlocked` fails-open (allows) when the
        // location can't be determined, so Android WebView users where the
        // IP lookup fails are never wrongly locked out.
        //
        // Exception: existing/already-verified users are grandfathered in and
        // may log in from ANY region — only brand-new sign-ups from unsupported
        // regions are blocked. We only trust PRE-verification signals here:
        //   • `verifiedUser` — the send-OTP `verifiedStatus` (now also captured
        //     on the 409 "OTP already sent" path, which previously dropped it
        //     and wrongly blocked genuine returning users), and
        //   • `verifyDetaiEEls` — the account has already completed onboarding,
        //     so it is definitely an existing user.
        // We deliberately do NOT read `verifiedStatus` off the verify response,
        // because that may flip to true for everyone once the OTP is verified.
        const isExistingVerifiedUser =
          verifiedUser === true ||
          response?.data?.user?.verifyDetails === true;

        if (isBlocked && !isExistingVerifiedUser) {
          localStorage.removeItem("token");
          setPopupType("failed");
          setRenderHtml(false);
          setPopupMessage(REGION_BLOCK_MESSAGE);
          setShowPopup(true);
          return;
        }

        localStorage.setItem("token", response?.data.token);

        // [FIX: origin-redirect] EXISTING-user domain redirect. If this account
        // has a stored home domain (appOrigin, set once at signup) and it is
        // DIFFERENT from the domain we're on now, send them to THEIR domain so
        // their per-domain localStorage session lands where it belongs.
        //   • new / pre-feature users → appOrigin null → this whole block is
        //     skipped → behaves EXACTLY as before (no redirect).
        //   • already on their own domain → same origin → skipped (no reload).
        // We go to the target domain's "/" (Start.js) because that route reliably
        // consumes ?token= + ?verifiedDetails= to re-establish the session there.
        const homeOrigin =
          response?.data?.user?.appOrigin ||
          response?.data?.defaultAppOrigin ||
          null;
        if (
          homeOrigin &&
          typeof window !== "undefined" &&
          homeOrigin !== window.location.origin
        ) {
          // Go to the target domain's /signup?token= — SignUp.js's own token
          // handler (fetchUserDetailsAndRoute) then routes CLEANLY: an onboarded
          // user lands straight on /fast-agent-detail, a not-yet-onboarded one on
          // the business step. We deliberately do NOT redirect to "/" (Start.js),
          // which clears storage and re-runs the onboarding flow → the ugly
          // signup→fast-agent→continue-setup flicker.
          const dest = new URL("/signup", homeOrigin);
          dest.searchParams.set("token", response?.data.token);
          dest.searchParams.set(
            "verifiedDetails",
            String(response?.data?.user?.verifyDetails === true)
          );
          // This user belongs to `homeOrigin`, NOT this domain. Remove every
          // SESSION-identity key we wrote on THIS domain (token set just above +
          // userEmail typed into the field + the onboarding/session keys) so no
          // ghost session lingers here after they leave. The real session is
          // re-established fresh on the target domain from the ?token= in the URL.
          //
          // We do NOT localStorage.clear() — that would also wipe the in-progress
          // OAuth handoff keys (oauth_redirect_uri / oauth_state / oauth_platform /
          // make_oauth_popup) and break a Make/Zapier "connect → login → return"
          // flow. So we remove only the identity keys, mirroring handleSwitchAccount.
          [
            "token",
            "userEmail",
            "onboardComplete",
            "paymentDone",
            "userId",
            "customerId",
            "businessId",
            "fetchEmail",
            "tokenFromAgentId",
            "teamOwnerId",
            "appsumoRedeemPending",
          ].forEach((k) => {
            try {
              localStorage.removeItem(k);
            } catch (_) { }
          });
          window.location.replace(dest.toString());
          return; // stop — the target domain's /signup token handler takes over
        }

        // Shopify App Store (Flow 1): a brand-new merchant who arrived via the
        // OAuth handoff. Read the buffered handoff token BEFORE sessionStorage.clear()
        // below wipes it, then finish linking their store now that they're
        // authenticated. Fire-and-forget — this must NEVER block or break signup.
        // const shopifyHandoff = sessionStorage.getItem("shopify_handoff_token");
        // if (shopifyHandoff) {
        //   finalizeShopifyConnection(shopifyHandoff)
        //     .then((r) => console.log("[shopify] connection finalized:", r?.data))
        //     .catch((e) =>
        //       console.warn("[shopify] finalize failed:", e?.response?.data || e.message)
        //     );
        // }
        // GA4: identify the user and record signup/login (email/OTP flow).
        const gaUserId = decodeToken(response?.data?.token)?.id;
        setGAUser(gaUserId);
        setUserProperties({
          account_type: response?.data?.paymentDone ? "paid" : "free_test",
          // free baseline; the server-side purchase event sets the real plan tier.
          ...(response?.data?.paymentDone ? {} : { plan: "none" }),
        });
        fireAuthEvent({
          isExisting: isExistingVerifiedUser,
          method: "email",
          token: response?.data?.token,
        });

        // Preserve the Make OAuth popup tag across this wipe. It is per-window, so
        // only the Make consent popup ever has it — a normal signup tab reads null
        // and keeps nothing. Without this, a brand-new user who signs up INSIDE the
        // Make popup would lose the tag and not auto-return to Make after onboarding.
        const makePopupTag = sessionStorage.getItem("make_oauth_popup");
        sessionStorage.clear();
        if (makePopupTag) sessionStorage.setItem("make_oauth_popup", makePopupTag);
        sessionStorage.setItem("referredBy", tempReferral);
        sessionStorage.setItem("selectedPlan", tempLandingSelectedPlan);
        sessionStorage.setItem("referredByName", tempReferredByName);
        sessionStorage.setItem("isUser", isUser);

        if (response?.data?.paymentDone) {
          localStorage.setItem("paymentDone", true);
        }
        localStorage.setItem(
          "showreferralfloating",
          response?.data?.user?.showreferralfloating
        );

        setPopupType("success");
        setShowPopup(true);
        setPopupMessage("One Time Password Verified successfully!");
        setRenderHtml(false);

        const userData = {
          name: response?.data?.user?.name || "",
          profile:
            `${API_BASE_URL?.split("/api")[0]}${response?.data?.user?.profile?.split("public")[1]
            }` ||
            response?.data?.user?.profile ||
            "images/camera-icon.avif",
          subscriptionDetails: {},
        };
        setUser(userData);
        didVerifyOtpRef.current = true;
        isRedirectingAfterOtpRef.current = true;
        const isTeamMember = !!response?.data?.user?.isTeamMember;

        // Team member ka onboarding skip — seedha grouped dashboard (multi-owner)
        if (isTeamMember) {
          localStorage.setItem("onboardComplete", "true");
          if (response?.data?.user?.ownerId) {
            localStorage.setItem("teamOwnerId", response.data.user.ownerId);
          }
          // See the Google branch above — the access picker decides the context,
          // then resolveHomeRoute decides the landing from that account's real
          // agent count.
          navigate("/select-access", { replace: true });
        } else {
          localStorage.removeItem("teamOwnerId");
          localStorage.setItem(
            "onboardComplete",
            response?.data?.user?.verifyDetails ? "true" : "false"
          );
          didVerifyOtpRef.current = true;

          const isOnboardComplete = !!response?.data?.user?.verifyDetails;
          const isPaymentDone = !!response?.data?.paymentDone;

          const goToNextStep = async () => {
            if (isPaymentDone) {
              if (isOnboardComplete) navigate("/steps", { replace: true });
              else skipToBusinessStep({ replace: true });
            }
            else if (response?.data?.agent) {
              sessionStorage.setItem("SelectAgentId", response?.data?.agent?.agent_id);
              sessionStorage.setItem("SelectAgentBusinessId", response?.data?.agent?.business_id);
              await getAgentDetailsAndBookings(response?.data?.agent?.agent_id || localStorage.getItem("tokenFromAgentId"), response?.data?.agent?.business_id || localStorage.getItem("businessId"))

              if (isOnboardComplete) navigate("/fast-agent-detail", { replace: true });
              else skipToBusinessStep({ replace: true });
            }
            else {
              if (isOnboardComplete) navigate("/fast-agent-detail", { replace: true });
              else skipToBusinessStep({ replace: true });
            }
          };

          // An AppSumo signup redeems its code first; the routing above runs
          // afterwards, from the redeem screen's onDone.
          const signupUtm = resolveSignupUtm(utm_data);
          // [FIX: appsumo-redeem-resume] The pending flag is the resume path.
          // It is set below on the first OTP and cleared ONLY when the user
          // redeems or skips (AppSumoRedeemPage), so while it is still set the
          // code is genuinely un-redeemed and the screen must come back.
          //
          // Needed because the user returns via /login → /signup with NO utm
          // params on the URL, and skipToBusinessStep has by then set
          // verifyDetails=true server-side — so every other "unfinished signup"
          // check reads the account as complete and routes them into the app
          // with the code still unredeemed.
          const redeemPending = (() => {
            try {
              return localStorage.getItem("appsumoRedeemPending") === "true";
            } catch {
              return false;
            }
          })();
          console.log(
            "[AppSumo] utm:", signupUtm,
            "→ redeem:", isAppSumoSignup(signupUtm),
            "| pending:", redeemPending
          );
          if (isAppSumoSignup(signupUtm) || redeemPending) {
            // The redeem step CANNOT render from here. Storing the token above
            // makes App.js's `/signup` route render a <Navigate> on its next
            // pass, which both unmounts this component and overrides the
            // navigate below — a <Navigate> runs during render and therefore
            // wins against a navigate() called from this async handler. So the
            // redeem screen lives on its own route, and this flag tells that
            // route guard to stand down until the user is done with it.
            //
            // localStorage, not state: App.js reads the token from localStorage
            // on every render, so the flag has to be readable the same way.
            localStorage.setItem("appsumoRedeemPending", "true");

            // The post-OTP routing decision is computed here (only this scope
            // knows the verify response) and handed over in navigation state,
            // for the redeem screen to run once the user is done.
            navigate("/appsumo-redeem", {
              replace: true,
              state: {
                afterRedeem: {
                  isPaymentDone,
                  isOnboardComplete,
                  agentId: response?.data?.agent?.agent_id || null,
                  businessId: response?.data?.agent?.business_id || null,
                },
              },
            });
          } else {
            await goToNextStep();
          }
        }

        //User Tracking
        trackStep({
          stepName: "otp_entered",
          screenName: "otp",
          email: localStorage.getItem("userEmail"),
          userId: decodeToken(localStorage.getItem("token"))?.id || null,
          data: { otp: fullOtp },
        });

        // GA4: onboarding_start fires AFTER otp_entered so in the timeline it
        // lands right after sign_up / otp_entered — carrying app_user_id (the
        // user is now identified via setGAUser above). The old splash-screen
        // firing ran before login and logged anonymously. New signups only.
        if (!isExistingVerifiedUser) {
          trackEvent("onboarding_start", { entry_point: "otp_verified" });
        }
      } else {
        setPopupType("failed");
        setShowPopup(true);
        setPopupMessage(
          "Failed to verify One Time Password. Please try again."
        );
        setRenderHtml(false);
      }
    } catch (error) {
      const msg = error?.response?.data?.error || "Internal Server Error";
      const isHtmlMessage = msg.includes("<a");

      setPopupType("failed");
      setShowPopup(true);
      setPopupMessage(msg);
      setRenderHtml(isHtmlMessage);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSendOTP = async () => {
    setEmailTouched(true);
    setEmailSubmitted(true);
    trackEvent("sign_up_start", { method: "email" });

    const isSpamEmail = spamDomains.some((domain) =>
      email?.includes(`${domain}`)
    );
    if (isSpamEmail) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Please enter a valid email address to continue...");
      return;
    }

    const emailValidationMsg = validateEmail(email);
    setEmailError(emailValidationMsg);

    if (emailValidationMsg) {
      return;
    }

    // 🌍 Region gate BEFORE sending the OTP. Out-of-region users are stopped
    // here so they never even receive an OTP — they just see the region popup
    // once. Fails-open when location is unknown, and is a no-op outside
    // production (the hook short-circuits in staging/dev).
    if (blockIfRegionNotAllowed()) return;

    setEmailError("");
    setIsVerifyingOtp(true);

    try {
      const recaptchaToken = await executeRecaptcha("send_otp");
      if (!recaptchaToken) {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage(
          "reCAPTCHA verification failed. Please refresh and try again."
        );
        return;
      }
      const utmPayload = utm_data ? utm_data : null;

      const response = await LoginWithEmailOTP(
        email,
        info,
        utmPayload,
        recaptchaToken
      );

      if (response?.status === 200) {
        // UTM already delivered to backend in this LoginWithEmailOTP call.
        // Mark it synced so the personal-details PUT (Details.js) does NOT
        // re-send it for OTP users — only Google/Apple users (who never send
        // UTM at login) will attach it there.
        if (utmPayload) localStorage.setItem("utmSynced", "true");

        setVerifiedUser(response.data.verifiedStatus);

        setShowPopup(true);
        setPopupType("success");
        setPopupMessage("One Time Password sent successfully!");

        setOtpSent(true);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.blur();

        const endTime = Date.now() + 300 * 1000;
        setResendEndTime(endTime);
        setIsResendDisabled(true);
        localStorage.setItem("otpResendEndTime", endTime.toString());
        //user tracker
        trackStep({
          stepName: "email_entered",
          screenName: "signup",
          email: email,
          userId: decodeToken(localStorage.getItem("token"))?.id || null,
          data: { email: email, info: info, utmPayload: utmPayload },
        });
      } else {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage("Failed to send One Time Password. Please try again.");
      }
    } catch (error) {
      // console.log("handleSendOTP error:", error);
      if (error?.response?.status === 409) {
        // "OTP already sent" — UTM was delivered on the prior LoginWithEmailOTP
        // call, so treat it as synced here too (covers cleared-localStorage
        // edge cases where the prior 200's flag was lost). Use component-scope
        // utm_data here — utmPayload is local to the try block above.
        if (utm_data) localStorage.setItem("utmSynced", "true");
        // "OTP already sent" — this path skips the 200 block above, so capture
        // verifiedStatus here too. Without this, a returning verified user who
        // re-requests an OTP (common when testing) loses the flag and gets
        // wrongly region-blocked at verify time.
        if (error?.response?.data?.verifiedStatus !== undefined) {
          setVerifiedUser(error.response.data.verifiedStatus);
        }
        setOtp(["", "", "", "", "", ""]);
        setOtpSent(true);

        setTimeout(() => {
          setPopupType("failed");
          setPopupMessage(
            "OTP already sent to your email. Please check inbox/spam and enter it below."
          );
          setShowPopup(true);
        }, 0);

        return;
      }

      else {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage(
          error?.response?.data?.error ||
          "Failed to send OTP. Please try again."
        );
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpChange = (value, index) => {
    setOtpInlineError("");

    // ✅ Paste handle karo
    if (value.length > 1) {
      const pastedValue = value.replace(/[^0-9]/g, "").slice(0, 6);
      const newOtp = [...otp];

      for (let i = 0; i < pastedValue.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedValue[i];
        }
      }
      setOtp(newOtp);

      // ✅ Focus last filled input
      const nextIndex = Math.min(index + pastedValue.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    // Single character (normal typing)
    const newOtp = [...otp];
    newOtp[index] = value.replace(/[^0-9]/g, "");
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (index === 5) {
      const fullOtp = newOtp.join("");
      if (fullOtp.length === 6) {
        inputRefs.current[index]?.blur();
        document.activeElement?.blur();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // ✅ Backspace - previous input pe jao
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    // ✅ Enter - Continue button trigger karo
    if (e.key === "Enter") {
      handleLoginClick();
    }
  };

  useEffect(() => {
    if (
      showPopup &&
      popupType === "success" &&
      popupMessage === "OTP sent successfully!"
    ) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showPopup, popupType, popupMessage]);

  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Don't show animation if we're in auth loading state
    if (loadingForAuth) return;

    const played = sessionStorage.getItem("loginAnimationPlayed");

    if (!played) {
      const delays = [150, 250, 350, 450, 550];
      const timers = delays.map((delay, index) =>
        setTimeout(() => setStep(index + 1), delay)
      );

      sessionStorage.setItem("loginAnimationPlayed", "true");

      setTimeout(() => {
        setReady(true);
      }, 700);

      return () => timers.forEach(clearTimeout);
    } else {
      setStep(5);
      setReady(true);
    }
  }, [loadingForAuth]);

  // Screen khulne par logo screen ke beech se nikalta hai — chhote se zoom
  // hokar thoda bada hota hai, pal bhar rukta hai, phir apni layout wali
  // jagah pe aa jaata hai (Onboarding wale logo jaisa). Sirf transform /
  // opacity: layout me logo shuru se apni asli jagah pe hi hai. 1.3s;
  // SignUp.module.css ke baaki entrance delay isi ke baad se shuru hote hain.
  const maskRef = useRef(null);
  useLayoutEffect(() => {
    const box = maskRef.current;
    const img = box?.querySelector("img");
    if (!ready || !box || !img || !box.animate) return;
    const r = img.getBoundingClientRect();
    const dy = window.innerHeight / 2 - (r.top + r.height / 2);
    const centre = `translateY(${dy}px)`;
    box.animate(
      [
        { opacity: 0, transform: `${centre} scale(0.6)`, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
        { opacity: 1, transform: `${centre} scale(1.15)`, offset: 0.42 },
        {
          opacity: 1,
          transform: `${centre} scale(1.15)`,
          offset: 0.54,
          easing: "cubic-bezier(0.65, 0, 0.35, 1)",
        },
        { opacity: 1, transform: "translateY(0) scale(1)" },
      ],
      { duration: 1300 },
    );
  }, [ready]);

  useEffect(() => {
    // add one history entry so back triggers popstate here
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      // Always go to onboarding step 3
      navigate("/?step=3", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);


  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("userEmail", email);
  }, [email]);

  useEffect(() => {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (!isMobile && otpSent && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [otpSent]);

  // useEffect(() => {
  //   const emailFromParams = searchParams.get("email");
  //   const otpNotRequired = searchParams.has("otp-not-required");

  //   if (emailFromParams) {
  //     const decodedEmail = decodeURIComponent(emailFromParams);
  //     setEmail(decodedEmail.toLowerCase());

  //     if (otpNotRequired) {
  //       setOtpSent(true);
  //       setOtp(["", "", "", "", "", ""]);
  //       inputRefs.current[0]?.blur();

  //       const storedEndTime = localStorage.getItem("otpResendEndTime");
  //       let endTime;

  //       if (storedEndTime && Date.now() < Number(storedEndTime)) {
  //         endTime = Number(storedEndTime);
  //       } else {
  //         endTime = Date.now() + 300 * 1000;
  //         localStorage.setItem("otpResendEndTime", endTime);
  //       }

  //       setResendEndTime(endTime);
  //       setIsResendDisabled(true);
  //     }
  //   }
  // }, [searchParams]);


  useEffect(() => {
    const emailFromParams = searchParams.get("email");
    if (!emailFromParams) return;

    const decodedEmail = decodeURIComponent(emailFromParams);
    setEmail(decodedEmail.toLowerCase());

    setOtpSent(true);
    setOtp(["", "", "", "", "", ""]);
    inputRefs.current[0]?.blur();

    const storedEndTime = localStorage.getItem("otpResendEndTime");
    let endTime = storedEndTime ? Number(storedEndTime) : null;

    if (!endTime || Date.now() >= endTime) {
      endTime = Date.now() + 300 * 1000;
      localStorage.setItem("otpResendEndTime", String(endTime));
    }

    setResendEndTime(endTime);
    setIsResendDisabled(true);
  }, [searchParams]);


  useEffect(() => {
    const msg = searchParams.get("msg");
    if (msg) {
      setOtpSent(true);
      setOtpInlineError(decodeURIComponent(msg));
      const params = new URLSearchParams(searchParams);
      params.delete("msg");
      params.delete("type");
      navigate(`/signup?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);


  const scrollListIntoView = () => {
    if (listRef.current) {
      listRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // Region gate for social logins. If the user is outside IN/US/CA we stop the
  // click here and show the popup instead of starting the OAuth redirect.
  // Fails-open (returns false) when location is unknown, so WebView users where
  // the IP lookup fails are never wrongly blocked.
  const blockIfRegionNotAllowed = () => {
    if (!isBlocked) return false;
    setPopupType("failed");
    setRenderHtml(false);
    setPopupMessage(REGION_BLOCK_MESSAGE);
    setShowPopup(true);
    return true;
  };

  // ─── Google One Tap (web browsers only) ─────────────────────────────
  // App WebView mein One Tap support nahi karta — wahan purana redirect
  // flow (chatkefun) hi chalega. Browser mein One Tap / GIS button.
  //
  // Token milne ke baad routing — wahi logic jaise token-from-URL flow
  // (team member / verifyDetails) karta hai, getUserDetails se.
  const routeAfterGoogleLogin = async (token) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/getUserDetails`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const gUser = res.data.user;

      setUser({
        name: gUser?.name || "",
        profile: gUser?.profile || "images/camera-icon.avif",
        subscriptionDetails: {},
      });
      if (gUser?.email) localStorage.setItem("userEmail", gUser.email);

      /*
       * GA4: identify the user and record sign_up / login.
       *
       * Ye block pehle SIRF `fetchUserDetailsAndRoute` me tha — yaani us
       * Google flow me jahan backend `/signup?token=…` par redirect karta hai.
       * One Tap / GIS button us raste se jata hi NAHI: wo `/auth/google/native`
       * se seedha token leta hai aur yahan aa jata hai. Aur web par default
       * yahi raasta hai (GIS button + auto One Tap prompt).
       *
       * Nateeja: Google se aane wale users ka sign_up / login kabhi fire hi
       * nahi hota tha, jabki unke baaki saare events theek chalte the.
       *
       * fireAuthEvent token se dedupe karta hai, isliye agar kisi aur path ne
       * pehle hi record kar diya to ye dobara nahi bhejta.
       */
      setGAUser(decodeToken(token)?.id);
      syncStoredPushToken(decodeToken(token)?.id);
      setUserProperties({
        account_type: gUser?.paymentDone ? "paid" : "free_test",
        ...(gUser?.paymentDone ? {} : { plan: "none" }),
      });
      fireAuthEvent({
        isExisting: gUser?.verifyDetails,
        method: "google",
        token,
      });
      // Sirf naye signup par — returning login par nahi.
      if (!gUser?.verifyDetails) {
        trackEvent("onboarding_start", { entry_point: "google_one_tap" });
      }

      // NOTE: do NOT return to Make here. Route into the app like email; the
      // return to Make happens later from FastAgentDetails, only once an agent
      // exists. The preserved make_oauth_popup tag makes that later return work.

      // Team member — onboarding skip → grouped Dashboard (multi-owner)
      if (gUser?.isTeamMember) {
        localStorage.setItem("onboardComplete", "true");
        if (gUser?.ownerId) localStorage.setItem("teamOwnerId", gUser.ownerId);
        navigate("/select-access", { replace: true });
        return;
      }

      localStorage.removeItem("teamOwnerId");

      localStorage.setItem(
        "onboardComplete",
        gUser?.verifyDetails ? "true" : "false"
      );
      if (gUser?.verifyDetails) navigate("/fast-agent-detail", { replace: true });
      else skipToBusinessStep({ replace: true, email: gUser?.email });
    } catch (err) {
      console.error("❌ Google login routing error:", err);
      setLoadingG(false);
      setPopupType("failed");
      setRenderHtml(false);
      setPopupMessage("Something went wrong. Please try again.");
      setShowPopup(true);
    }
  };

  // One Tap / GIS credential callback — `credential` ek Google ID token
  // hai, jise hum existing /auth/google/native endpoint pe verify karwate
  // hain. Wahi endpoint customerId / ZapApikey / client_id bana ke save
  // karta hai aur app ka JWT return karta hai (no Calendar/Contacts scope —
  // woh app ke andar se on-demand sync hote hain).
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) return;

    // Region gate — same as chatkefun.
    if (blockIfRegionNotAllowed()) return;

    setLoadingG(true);
    try {
      const platform = detectPlatform();
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/google/native`,
        { idToken: credential, platform }
      );

      const token = res?.data?.token;
      if (!token) throw new Error("No token in response");

      // Preserve the Make OAuth popup tag across this wipe (same guarded pattern
      // as the OTP flow). Restored ONLY if it already existed → a normal One Tap
      // sign-in restores nothing and clears fully as before; a One Tap sign-in
      // INSIDE the Make popup keeps the tag so it still auto-returns to Make.
      const makePopupTag = sessionStorage.getItem("make_oauth_popup");
      sessionStorage.clear();
      if (makePopupTag) sessionStorage.setItem("make_oauth_popup", makePopupTag);
      localStorage.setItem("token", token);
      localStorage.setItem(
        "onboardComplete",
        res?.data?.verifiedDetails || "false"
      );

      // [FIX: origin-redirect] Google One Tap / GIS path — SAME behaviour as the
      // OTP login: if this account's stored home domain (appOrigin) differs from
      // the domain we're on now, send them to THEIR domain (carrying the token,
      // via /signup?token= which routes cleanly). We clear this domain's identity
      // keys first so no ghost session lingers here. null appOrigin (new /
      // pre-feature) → skipped → unchanged. Same-domain → skipped (no reload).
      const homeOrigin = res?.data?.appOrigin || res?.data?.defaultAppOrigin || null;
      if (
        homeOrigin &&
        typeof window !== "undefined" &&
        homeOrigin !== window.location.origin
      ) {
        const dest = new URL("/signup", homeOrigin);
        dest.searchParams.set("token", token);
        dest.searchParams.set(
          "verifiedDetails",
          String(res?.data?.verifiedDetails === "true" || res?.data?.verifiedDetails === true)
        );
        [
          "token",
          "userEmail",
          "onboardComplete",
          "paymentDone",
          "userId",
          "customerId",
          "businessId",
          "fetchEmail",
          "tokenFromAgentId",
          "teamOwnerId",
          "appsumoRedeemPending",
        ].forEach((k) => {
          try {
            localStorage.removeItem(k);
          } catch (_) { }
        });
        window.location.replace(dest.toString());
        return; // stop — the target domain takes over
      }

      await routeAfterGoogleLogin(token);
    } catch (err) {
      console.error("❌ Google One Tap login failed:", err);
      setLoadingG(false);
      setPopupType("failed");
      setRenderHtml(false);
      setPopupMessage(
        err?.response?.data?.message || "Google login failed. Please try again."
      );
      setShowPopup(true);
    }
  };

  // Latest-handler ref taaki GIS callback hamesha fresh state (isBlocked
  // etc.) padhe — warna initialize ke waqt ka stale closure use hota.
  const credentialHandlerRef = useRef(handleGoogleCredential);
  credentialHandlerRef.current = handleGoogleCredential;

  // Load Google Identity Services + One Tap prompt (browser only).
  useEffect(() => {
    // [TEMP DIAGNOSTIC] — confirm naya code load hua. Test ke baad hata denge.
    console.log(
      "🟣 ONE-TAP NEW CODE LOADED | isWebView:",
      !!window.ReactNativeWebView,
      "| clientId set:",
      !!process.env.REACT_APP_GOOGLE_CLIENT_ID
    );
    if (window.ReactNativeWebView) return; // app WebView → chatkefun
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn(
        "REACT_APP_GOOGLE_CLIENT_ID missing — Google One Tap disabled"
      );
      return;
    }

    const initGsi = () => {
      if (!window.google?.accounts?.id || gsiInitialized.current) return;
      gsiInitialized.current = true;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => credentialHandlerRef.current(resp),
        use_fedcm_for_prompt: true,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Sirf tab auto popup dikhao jab user pehle se logged-in NA ho —
      // warna login ke baad bhi One Tap suggestion aa jata tha.
      if (!localStorage.getItem("token")) {
        window.google.accounts.id.prompt(); // One Tap auto popup
      }
      setGsiReady(true);
    };

    const existing = document.getElementById("google-gsi-script");
    if (existing) {
      if (window.google?.accounts?.id) initGsi();
      else existing.addEventListener("load", initGsi);
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.id = "google-gsi-script";
      script.onload = initGsi;
      document.body.appendChild(script);
    }
  }, []);

  // Render the GIS "Continue with Google" button once GIS is ready AND the
  // target div is mounted (depends on `ready` animation + isLoadingG toggle).
  useEffect(() => {
    if (!gsiReady) return;
    if (!googleBtnRef.current) return;
    if (!window.google?.accounts?.id) return;

    googleBtnRef.current.innerHTML = ""; // avoid duplicate render
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      shape: "pill",
      text: "continue_with",
      logo_alignment: "center",
    });
  }, [gsiReady, ready, isLoadingG]);

  const chatkefun = () => {
    if (blockIfRegionNotAllowed()) return;
    trackEvent("sign_up_start", { method: "google" });
    setLoadingG(true);
    // Store timestamp when user initiates Google auth
    sessionStorage.setItem('googleAuthStartTime', Date.now().toString());

    // Detect once and forward to the backend through a query string so it
    // can persist EndUser.platform after the OAuth callback resolves.
    const platform = detectPlatform();

    if (window.ReactNativeWebView) {
      const googleAuthUrl = `${process.env.REACT_APP_API_BASE_URL}/auth/google?isMobile=true&flow=login&platform=${platform}`;
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'GOOGLE_SIGN_IN', url: googleAuthUrl })
      );
    } else {
      const isAppDomain = window.location.hostname === 'app.rxpt.ai';
      const flow = isAppDomain ? 'next' : '';
      const base = `${process.env.REACT_APP_API_BASE_URL}/auth/google`;
      // [FIX: origin-redirect] — do frontend (3000/3001, live app.rexpt.in/
      // app.rxpt.ai) me login ke baad usi frontend pe wapas laane ke liye origin
      // bhejo. REVERT: neeche `&origin=...` hata do.
      const originQs = `&origin=${encodeURIComponent(window.location.origin)}`;
      const url = flow
        ? `${base}?flow=${flow}&platform=${platform}${originQs}`
        : `${base}?platform=${platform}${originQs}`;
      // [FIX: origin-redirect] The Google flow navigates AWAY from this domain and
      // the backend may redirect the user to a DIFFERENT home domain (their
      // appOrigin). If so, whatever we wrote on THIS domain (the typed userEmail
      // + any prior session keys) would be left behind here as a ghost — exactly
      // like the OTP flow used to leave data. Google uses its own account picker,
      // NOT this typed email, so clearing these identity keys is safe and never
      // affects Google auth. We do NOT localStorage.clear() (that would wipe the
      // in-progress OAuth handoff keys for Make/Zapier); we remove only identity
      // keys — mirroring the OTP redirect cleanup.
      [
        "token",
        "userEmail",
        "onboardComplete",
        "paymentDone",
        "userId",
        "customerId",
        "businessId",
        "fetchEmail",
        "tokenFromAgentId",
        "teamOwnerId",
        "appsumoRedeemPending",
      ].forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch (_) { }
      });
      window.location.href = url;
    }
  };
  const handleAppleSignIn = () => {
    if (blockIfRegionNotAllowed()) return;
    trackEvent("sign_up_start", { method: "apple" });
    // Check if running in iOS WebView
    const isIOSWebView = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipod|ipad/.test(userAgent);
      const isWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
      return isIOS && isWebView;
    };

    //  setLoadingG(true);

    sessionStorage.setItem('googleAuthStartTime', Date.now().toString());

    // Check if running in React Native WebView
    if (window.ReactNativeWebView) {
      // Add mobile=true parameter for mobile app
      const appleAuth = `${process.env.REACT_APP_API_BASE_URL}/auth/apple?mobile=true&flow=login`;

      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'APPLE_SIGN_IN',
          url: appleAuth
        })
      );
    } else {
      // Normal web browser - redirect as usual (no mobile parameter)
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}/auth/apple?mobile=false&flow=login`;
    }
  };

  // Handle Google OAuth loading state and back button
  useEffect(() => {
    const authStartTime = sessionStorage.getItem('googleAuthStartTime');

    if (authStartTime) {
      const elapsed = Date.now() - parseInt(authStartTime);
      // If more than 2 seconds have passed, user likely came back
      if (elapsed > 2000) {
        setLoadingG(false);
      }
      sessionStorage.removeItem('googleAuthStartTime');
    } else {
      setLoadingG(false);
    }

    // Handle back button navigation
    const handlePageShow = (event) => {
      // Check if page was loaded from cache (back/forward button)
      if (event.persisted) {
        setLoadingG(false);
        setLoadingForAuth(false); // Also reset auth loading
        sessionStorage.removeItem('googleAuthStartTime');
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // ─── RN OAUTH_CANCELLED bridge ────────────────────────────
  // pageshow/visibilitychange don't fire inside the WebView when
  // InAppBrowser is dismissed, so the spinner would stay forever
  // without this listener. App.tsx posts OAUTH_CANCELLED on dismiss.
  useEffect(() => {
    const handleRNMessage = (event) => {
      try {
        const msg =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (!msg?.__fromRN) return;
        if (msg.type !== 'OAUTH_CANCELLED') return;

        const flow = msg.data?.flow;
        if (flow && flow !== 'login') return; // contacts/calendar handled elsewhere
        setLoadingG(false);
        setLoadingForAuth?.(false);
        sessionStorage.removeItem('googleAuthStartTime');
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener('message', handleRNMessage);
    document.addEventListener('message', handleRNMessage);
    return () => {
      window.removeEventListener('message', handleRNMessage);
      document.removeEventListener('message', handleRNMessage);
    };
  }, []);

  // Show loader while mmmauthenticating
  if (loadingForAuth) {
    return (

      <Loader2 />

    );
  }

  return (
    <>
      <>
        {ready && (
          <>
            <div className={styles.signUpContainer}>
              {/* <div className={styles.StartMain}>
                  <div>
                    <img src="images/Ellipse 6.png" alt="" />
                    <img src="images/Ellipse 7.png" alt="" />
                    <img src="images/Ellipse 8.png" alt="" />
                    <img src="images/Ellipse 9.png" alt="" />
                    <img src="images/Ellipse 10.png" alt="" />
                    <img src="images/Ellipse 11.png" alt="" />
                  </div>
                </div> */}

              <div className={styles.pageEnterAnimation}>
                <div className={styles.mask} ref={maskRef}>
                  <img src="svg/Rexpt-Logo.svg" alt="Mask" />
                </div>

                <div
                  className={`${styles.logimg} ${step >= 1 ? styles.animate1 : ""
                    }`}
                >
                  {/* <img
                      className={styles.logo}
                      src="svg/Rexpt-Logo.svg"
                      alt="Rexpt-Logo"
                    /> */}
                </div>

                <div
                  className={`${styles.Maincontent} ${step >= 2 ? styles.animate2 : ""
                    }`}
                >
                  <div className={styles.welcomeTitle}>
                    <h1>Log In to your<br /> Account</h1>
                    <p>
                      If it doesn't exist, we'll create one for you completely
                      free!
                    </p>
                  </div>
                </div>

                <div className={styles.container} ref={listRef}>
                  {/* {!otpSent && (
                      <>
                        <div
                          className={`${styles.labReq} ${step >= 3 ? styles.animate3 : ""
                            }`}
                        >
                          <div className={styles.Dblock}>
                            <input
                              type="email"
                              className={`${styles.emailInput} ${emailError ? styles.inputError : ""
                                }`}
                              placeholder="Johnvick@gmail.com"
                              value={email}
                              onChange={handleEmailChange}
                              required
                            />
                          </div>
                          {emailError && (
                            <p className={styles.inlineError}>{emailError}</p>
                          )}
                        </div>

                        <br />

                        <div
                          className={`${styles.btnTheme} ${step >= 4 ? styles.animate4 : ""
                            }`}
                          onClick={handleSendOTP}
                        >
                          <AnimatedButton
                            isLoading={isVerifyingOtp}
                            label="Send One Time Password"
                            position={{ position: "relative" }}
                          />
                        </div>
                      </>
                    )} */}

                  {otpSent && (
                    <>
                      {email && (
                        <p className={`${styles.codeText} ${styles.suItem} ${styles.suD1}`}>
                          Email has been sent to <b>{email}</b>
                        </p>
                      )}

                      <p className={`${styles.codeText} ${styles.suItem} ${styles.suD2}`}>
                        Enter the code sent to your email
                      </p>

                      <div className={`${styles.otpContainer} ${styles.suOtp}`}>
                        {[...Array(6)].map((_, i) => (
                          <input
                            key={i}
                            id={`otp-${i}`}
                            maxLength="6"
                            value={otp[i]}
                            onChange={(e) => handleOtpChange(e.target.value, i)}
                            className={styles.otpInput}
                            onKeyDown={(e) => handleKeyDown(e, i)}
                            ref={(el) => (inputRefs.current[i] = el)}
                            onPaste={(e) => {
                              // ✅ Paste event handle karo
                              e.preventDefault();
                              const pastedData = e.clipboardData
                                .getData("text")
                                .replace(/[^0-9]/g, "")
                                .slice(0, 6);

                              if (pastedData) {
                                const newOtp = ["", "", "", "", "", ""];
                                for (let j = 0; j < pastedData.length; j++) {
                                  newOtp[j] = pastedData[j];
                                }
                                setOtp(newOtp);

                                // ✅ Last filled input pe focus karo
                                const lastIndex = Math.min(pastedData.length - 1, 5);
                                inputRefs.current[lastIndex]?.focus();
                              }
                            }}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, "");
                            }}
                            onClick={() => setTimeout(scrollListIntoView, 300)}
                            inputMode="numeric"
                            type="tel"
                          />
                        ))}
                      </div>
                      {otpInlineError && (
                        <div className={styles.otpInlineError}>{otpInlineError}</div>
                      )}

                      <p className={`${styles.SpamMessage} ${styles.suItem} ${styles.suD4}`}>
                        Please check your spam folder if you don't find it in
                        your main inbox.
                      </p>

                      <div className={`${styles.suItem} ${styles.suD5}`}>
                        <button
                          type="button"
                          className={styles.resendButton}
                          onClick={handleSendOTP}
                          disabled={isResendDisabled}
                          style={{
                            cursor: isResendDisabled
                              ? "not-allowed"
                              : "pointer",
                            opacity: isResendDisabled ? 1 : 1,
                            background: "none",
                            border: "none",
                            color: "#6524EB",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          {isResendDisabled && resendTimer > 0
                            ? ` ${String(
                              Math.floor(resendTimer / 60)
                            ).padStart(2, "0")}:${String(
                              resendTimer % 60
                            ).padStart(2, "0")}`
                            : "Resend One Time Password"}
                        </button>
                      </div>

                      <br />

                      <div className={`${styles.Btn} ${styles.suItem} ${styles.suD6}`} onClick={handleLoginClick}>
                        <div type="submit">
                          <div className={styles.btnTheme}>
                            <AnimatedButton
                              isLoading={isVerifyingOtp}
                              label="Continue"
                              position={{ position: "relative" }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div
                    className={`${styles.Maincontent2} ${step >= 5 ? styles.animate5 : ""
                      }`}
                  >
                    <div className={styles.divider}>
                      <hr className={styles.line} />
                      <span className={styles.text}>Or continue with</span>
                      <hr className={styles.line} />
                    </div>

                    <div className={styles.socialMedia}>
                      {isLoadingG ? (
                        <Loader size="30" />
                      ) : window.ReactNativeWebView ? (
                        // App WebView — One Tap support nahi, purana redirect flow
                        <img
                          onClick={chatkefun}
                          src="svg/Google-icon02.svg"
                          alt="Continue with Google"
                          style={{ cursor: "pointer" }}
                        />
                      ) : (
                        // Web browser — Google One Tap / GIS button yahan render hota hai
                        <div ref={googleBtnRef} />
                      )}
                    </div>

                    {/* <div onClick={handleAppleSignIn} style={{ cursor: 'pointer' }}>
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="currentColor"/>
    </svg>
  </div> */}
                    <p className={styles.PrivaceTerms}>
                      By creating an account, you agree to <br /> Rexpt&nbsp;
                      <a
                        href="https://www.rexpt.in/Terms-Condition"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms & Conditions
                      </a>
                      &nbsp;and&nbsp;
                      <a
                        href="https://www.rexpt.in/Privacy-Policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </div>

                  {showPopup && (
                    <PopUp
                      type={popupType}
                      onClose={() => setShowPopup(false)}
                      message={popupMessage}
                      renderHTML={true}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </>

    </>
  );
};

export default SignUp;
